import httpclient from 'libs/Axios';

const STATUS_MAP: Record<string, string> = {
  NEW: 'new',
  IN_DEMO: 'in-demo',
  CONSIDERED: 'considering',
  CONSIDERING: 'considering',
  CONVERTED: 'decided',
  DECIDED: 'decided',
};

const AVATAR_BACKGROUNDS = [
  'linear-gradient(135deg,#fde68a,#f59e0b)',
  'linear-gradient(135deg,#bfdbfe,#3b82f6)',
  'linear-gradient(135deg,#fbcfe8,#ec4899)',
  'linear-gradient(135deg,#bbf7d0,#10b981)',
  'linear-gradient(135deg,#ddd6fe,#8b5cf6)',
  'linear-gradient(135deg,#fed7aa,#f97316)',
];

const titleCase = (value = '') => value
  .toLowerCase()
  .replace(/_/g, ' ')
  .replace(/\b\w/g, character => character.toUpperCase());

const initialsFor = (value = '') => value
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part[0]?.toUpperCase())
  .join('') || 'LE';

const formatVisitedAt = (value?: string) => {
  if (!value) return { enquired: 'Date unavailable', enquiredAgo: '—' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { enquired: 'Date unavailable', enquiredAgo: '—' };

  const diffMinutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  const enquiredAgo = diffMinutes < 1
    ? 'Now'
    : diffMinutes < 60
      ? `${diffMinutes}m ago`
      : diffMinutes < 1440
        ? `${Math.floor(diffMinutes / 60)}h ago`
        : `${Math.floor(diffMinutes / 1440)}d ago`;

  return {
    enquired: date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }),
    enquiredAgo,
  };
};

const mapLead = (lead: any, index: number) => {
  const demosTriggered = [
    lead.is_inspired_365 && 'inspire',
    lead.is_child_care && 'childcare',
    lead.is_student_info && 'progress',
  ].filter(Boolean);
  const child = titleCase(lead.student_name) || 'Student name unavailable';
  const name = titleCase(lead.parent_name) || 'Parent name unavailable';
  const visited = formatVisitedAt(lead.visited_at);

  return {
    id: `lead-${lead.lead_id}`,
    leadId: lead.lead_id,
    name,
    child,
    childAge: Number(lead.student_age) || 0,
    gender: lead.student_gender || '',
    grade: titleCase(lead.joining_class) || 'Class unavailable',
    source: titleCase(lead.utm_source) || 'Direct',
    phone: lead.mobile_number || 'Not provided',
    email: lead.email_id || '',
    language: '',
    tags: [],
    concern: '',
    status: STATUS_MAP[String(lead.lead_status || '').toUpperCase()] || 'new',
    ...visited,
    demosTriggered,
    score: 0,
    avatarBg: AVATAR_BACKGROUNDS[index % AVATAR_BACKGROUNDS.length],
    initials: initialsFor(child),
    detailsLoaded: false,
  };
};

export const fetchAdmissionLeads = async (authData: any) => {
  if (!authData?.INSTITUTE_ID || !authData?.BRANCH_ID) {
    throw new Error('Institute or branch session is unavailable. Please sign in again.');
  }

  const response = await httpclient().get('/master/leads/list', {
    headers: {
      'branch-id': authData.BRANCH_ID,
      'institute-id': authData.INSTITUTE_ID,
    },
  });

  if (response?.data?.code !== 200) {
    throw new Error(response?.data?.message || 'Unable to load admission leads.');
  }

  const leads = response?.data?.data?.Leads;
  return Array.isArray(leads) ? leads.map(mapLead) : [];
};

export const fetchAdmissionLeadDetails = async (leadId: number, authData: any) => {
  if (!leadId) throw new Error('A valid lead is required.');
  if (!authData?.INSTITUTE_ID || !authData?.BRANCH_ID) {
    throw new Error('Institute or branch session is unavailable. Please sign in again.');
  }

  const response = await httpclient().get(`/comm/admissions_assured/view?lead_id=${leadId}`, {
    headers: {
      'branch-id': authData.BRANCH_ID,
      'institute-id': authData.INSTITUTE_ID,
    },
  });

  if (response?.data?.code !== 200 || !response?.data?.data) {
    throw new Error(response?.data?.message || 'Unable to load lead details.');
  }

  const lead = response.data.data;
  const demosTriggered = [
    lead.IS_INSPIRED_365 && 'inspire',
    lead.IS_CHILD_CARE && 'childcare',
    lead.IS_STUDENT_INFO && 'progress',
  ].filter(Boolean);
  const child = titleCase(lead.STUDENT_NAME) || 'Student name unavailable';
  const content = lead.CONTENT || {};
  const studentInsight = content?.AI_STUDENT_INFORMATION?.MESSAGE;

  return {
    leadId: lead.LEAD_ID,
    name: titleCase(lead.PARENT_NAME) || 'Parent name unavailable',
    child,
    childAge: Number(lead.STUDENT_AGE) || 0,
    gender: lead.STUDENT_GENDER || '',
    grade: titleCase(lead.JOINING_CLASS) || 'Class unavailable',
    currentSchool: lead.CURRENT_SCHOOL || '',
    address: lead.ADDRESS || '',
    email: lead.EMAIL_ID || '',
    phone: lead.MOBILE_NUMBER || 'Not provided',
    concern: studentInsight || '',
    tags: [],
    demosTriggered,
    content,
    initials: initialsFor(child),
    detailsLoaded: true,
    profileFields: [
      { label: 'Student profile', available: Boolean(lead.STUDENT_NAME) },
      { label: 'Parent details', available: Boolean(lead.PARENT_NAME) },
      { label: 'Age data', available: Number(lead.STUDENT_AGE) > 0 },
      { label: 'Grade data', available: Boolean(lead.JOINING_CLASS) },
      { label: 'Current school', available: Boolean(lead.CURRENT_SCHOOL) },
    ],
  };
};
